import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { heatOf, isOpen, type Ticket } from './mock';

const PRIMARY = '2563EB';

export type Agg = {
  total: number;
  open: number;
  breached: number;
  atRisk: number;
  onTrack: number;
  byStatus: [string, number][];
  byPriority: [string, number][];
  byTeam: [string, number][];
  byCategory: [string, number][];
  byModule: [string, number][];
};

function countBy(items: Ticket[], key: (t: Ticket) => string): [string, number][] {
  const m = new Map<string, number>();
  for (const it of items) m.set(key(it), (m.get(key(it)) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

export function aggregate(tickets: Ticket[], now: number): Agg {
  const breached = tickets.filter((t) => now > t.slaDueAt).length;
  const atRisk = tickets.filter((t) => heatOf(t, now) >= 0.65 && now <= t.slaDueAt).length;
  return {
    total: tickets.length,
    open: tickets.filter(isOpen).length,
    breached,
    atRisk,
    onTrack: tickets.length - breached - atRisk,
    byStatus: countBy(tickets, (t) => t.state),
    byPriority: countBy(tickets, (t) => t.priority),
    byTeam: countBy(tickets, (t) => t.team),
    byCategory: countBy(tickets, (t) => t.category),
    byModule: countBy(tickets, (t) => t.module),
  };
}

function slaLabel(t: Ticket, now: number) {
  if (now > t.slaDueAt) return 'Breached';
  if (heatOf(t, now) >= 0.65) return 'At risk';
  return 'On track';
}

function fmt(ms: number) {
  return new Date(ms).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Render a simple bar chart to a PNG data URL for embedding in Excel. */
function barChart(title: string, data: [string, number][]): string {
  const W = 560;
  const H = 280;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#0f172a';
  ctx.font = '600 15px Inter, sans-serif';
  ctx.fillText(title, 16, 26);

  const rows = data.slice(0, 8);
  const max = Math.max(...rows.map((r) => r[1]), 1);
  const top = 48;
  const left = 150;
  const barH = 20;
  const gap = 10;
  ctx.font = '12px Inter, sans-serif';
  rows.forEach(([label, v], i) => {
    const y = top + i * (barH + gap);
    ctx.fillStyle = '#475569';
    ctx.textAlign = 'right';
    ctx.fillText(label.length > 20 ? label.slice(0, 19) + '…' : label, left - 10, y + 14);
    ctx.textAlign = 'left';
    ctx.fillStyle = '#' + PRIMARY;
    const w = ((W - left - 50) * v) / max;
    ctx.fillRect(left, y, Math.max(2, w), barH);
    ctx.fillStyle = '#0f172a';
    ctx.fillText(String(v), left + w + 6, y + 14);
  });
  return c.toDataURL('image/png');
}

function slug(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// ---------------- Excel ----------------
export async function exportExcel(tickets: Ticket[], now: number, scope = 'All teams') {
  const agg = aggregate(tickets, now);
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Compass';

  // ---- raw data sheet (pivot-ready: AutoFilter on) ----
  const ws = wb.addWorksheet('Tickets', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = [
    { header: 'Ref', key: 'ref', width: 12 },
    { header: 'Subject', key: 'subject', width: 48 },
    { header: 'Module', key: 'module', width: 16 },
    { header: 'Team', key: 'team', width: 20 },
    { header: 'Priority', key: 'priority', width: 10 },
    { header: 'Status', key: 'status', width: 16 },
    { header: 'Category', key: 'category', width: 16 },
    { header: 'Assignee', key: 'assignee', width: 14 },
    { header: 'Requester', key: 'requester', width: 20 },
    { header: 'SLA', key: 'sla', width: 12 },
    { header: 'Created', key: 'created', width: 20 },
    { header: 'SLA Due', key: 'due', width: 20 },
  ];
  tickets.forEach((t) =>
    ws.addRow({
      ref: t.ref,
      subject: t.subject,
      module: t.module,
      team: t.team,
      priority: t.priority,
      status: t.state,
      category: t.category,
      assignee: t.assignee ?? 'Unassigned',
      requester: t.requester,
      sla: slaLabel(t, now),
      created: fmt(t.createdAt),
      due: fmt(t.slaDueAt),
    }),
  );
  const header = ws.getRow(1);
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + PRIMARY } };
  header.alignment = { vertical: 'middle' };
  ws.autoFilter = { from: 'A1', to: 'L1' };

  // ---- summary sheet (pivot tables + charts) ----
  const sum = wb.addWorksheet('Summary');
  sum.getColumn(1).width = 22;
  sum.getColumn(2).width = 12;
  sum.getColumn(3).width = 10;

  let r = 1;
  const title = sum.getCell(`A${r}`);
  title.value = `Compass — ${scope} Report`;
  title.font = { bold: true, size: 16, color: { argb: 'FF' + PRIMARY } };
  r += 1;
  sum.getCell(`A${r}`).value = `Generated ${fmt(now)}`;
  sum.getCell(`A${r}`).font = { italic: true, color: { argb: 'FF64748B' } };
  r += 2;

  // KPI row
  const kpis: [string, number | string][] = [
    ['Total', agg.total],
    ['Open', agg.open],
    ['Breached', agg.breached],
    ['At risk', agg.atRisk],
    ['On track', agg.onTrack],
  ];
  kpis.forEach(([k, v], i) => {
    const col = i + 1;
    const kc = sum.getCell(r, col);
    kc.value = k;
    kc.font = { color: { argb: 'FF64748B' }, size: 10 };
    const vc = sum.getCell(r + 1, col);
    vc.value = v;
    vc.font = { bold: true, size: 18 };
  });
  r += 3;

  const writePivot = (heading: string, rows: [string, number][]) => {
    const h = sum.getCell(`A${r}`);
    h.value = heading;
    h.font = { bold: true, size: 12 };
    r += 1;
    const head = sum.getRow(r);
    head.getCell(1).value = 'Value';
    head.getCell(2).value = 'Count';
    head.getCell(3).value = '%';
    head.font = { bold: true };
    head.eachCell((c) => (c.border = { bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } } }));
    r += 1;
    const total = rows.reduce((a, b) => a + b[1], 0) || 1;
    rows.forEach(([label, v]) => {
      const row = sum.getRow(r);
      row.getCell(1).value = label;
      row.getCell(2).value = v;
      row.getCell(3).value = Math.round((v / total) * 100) / 100;
      row.getCell(3).numFmt = '0%';
      r += 1;
    });
    r += 1;
  };
  writePivot('By status', agg.byStatus);
  writePivot('By priority', agg.byPriority);
  writePivot('By team', agg.byTeam);
  writePivot('By category', agg.byCategory);

  // embedded charts
  const img1 = wb.addImage({ base64: barChart('Tickets by team', agg.byTeam), extension: 'png' });
  sum.addImage(img1, { tl: { col: 4, row: 4 }, ext: { width: 460, height: 230 } });
  const img2 = wb.addImage({ base64: barChart('Tickets by status', agg.byStatus), extension: 'png' });
  sum.addImage(img2, { tl: { col: 4, row: 17 }, ext: { width: 460, height: 230 } });

  const buf = await wb.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `compass-report-${slug(scope)}.xlsx`,
  );
}

// ---------------- PDF ----------------
export function exportPdf(tickets: Ticket[], now: number, scope = 'All teams') {
  const agg = aggregate(tickets, now);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pw = doc.internal.pageSize.getWidth();

  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, pw, 70, 'F');
  doc.setTextColor(255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(`Compass — ${scope}`, 40, 38);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Generated ${fmt(now)}`, 40, 56);

  // KPI band
  doc.setTextColor(15, 23, 42);
  const kpis: [string, string | number][] = [
    ['Total', agg.total],
    ['Open', agg.open],
    ['Breached', agg.breached],
    ['At risk', agg.atRisk],
    ['On track', agg.onTrack],
  ];
  let x = 40;
  const ky = 100;
  const kw = (pw - 80) / kpis.length;
  kpis.forEach(([k, v]) => {
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(String(k).toUpperCase(), x, ky);
    doc.setFontSize(20);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.text(String(v), x, ky + 22);
    doc.setFont('helvetica', 'normal');
    x += kw;
  });

  const tbl = (title: string, rows: [string, number][], startY: number) => {
    const total = rows.reduce((a, b) => a + b[1], 0) || 1;
    autoTable(doc, {
      startY,
      head: [[title, 'Count', '%']],
      body: rows.map(([l, v]) => [l, String(v), `${Math.round((v / total) * 100)}%`]),
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      styles: { fontSize: 9, cellPadding: 4 },
      margin: { left: 40, right: 40 },
    });
    // @ts-expect-error autotable attaches lastAutoTable
    return doc.lastAutoTable.finalY as number;
  };

  let y = 140;
  y = tbl('By status', agg.byStatus, y) + 16;
  y = tbl('By priority', agg.byPriority, y) + 16;
  y = tbl('By team', agg.byTeam, y) + 16;

  // breaching / critical tickets
  const hot = tickets
    .filter((t) => now > t.slaDueAt || t.priority === 'P1')
    .slice(0, 14)
    .map((t) => [t.ref, t.subject.slice(0, 48), t.team, t.priority, slaLabel(t, now)]);
  if (hot.length) {
    autoTable(doc, {
      startY: y,
      head: [['Ref', 'Subject', 'Team', 'Pri', 'SLA']],
      body: hot,
      theme: 'grid',
      headStyles: { fillColor: [220, 38, 38] },
      styles: { fontSize: 8, cellPadding: 3 },
      margin: { left: 40, right: 40 },
    });
  }

  doc.save(`compass-report-${slug(scope)}.pdf`);
}
